import React, { useState } from "react";
import { IconLoader2, IconMessage2 } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { BookingApprovalAction } from "@/app/booking-flow/publicBookingFlowTypes";
import { isCodeRejected } from "@/app/booking-flow/publicBookingFlowTypes";

// Deliberately says "if": the server answers a wrong confirmation number with the same 202 as a right
// one (a decoy that is never sent), so this page cannot know whether a code went out and must not
// imply that the phone number and confirmation number matched anything.
export const APPROVAL_CODE_SENT_COPY =
  "If the phone number and confirmation number match an appointment, we sent a six-digit code by text message, and by email when we have an address. If we could not text you, we will call you with the code.";
export const APPROVAL_CODE_CANCEL_COPY = "Entering the code cancels the appointment.";

const VERIFY_LOCKED_OUT_MESSAGE = "Too many attempts. Please wait a few minutes and try again.";
const VERIFY_GENERIC_MESSAGE = "Something went wrong. Please try again.";

export type ApprovalCodeError = { kind: "rejected"; guessesRemaining: number } | { kind: "other"; message: string };

/**
 * What a failed POST api/ams/booking-approvals/{id}/verify means for the page.
 * - `rejected`: a wrong code with guesses left. Zero left means the approval has failed; pages treat
 *   that like `gone`.
 * - `gone`: 410 notPending (expired, finished or out of guesses) or 404 (not this session's, or aged
 *   out). The page shows its existing expired view.
 * - `notCodeMode`: 409; approvals went back to phone calls while the page was open.
 * - `other`: anything else, with a message to show under the code box.
 */
export type VerifyFailure =
  | { kind: "rejected"; guessesRemaining: number }
  | { kind: "gone" }
  | { kind: "notCodeMode" }
  | { kind: "other"; message: string };

export function readVerifyFailure(error: unknown): VerifyFailure {
  if (isCodeRejected(error)) {
    return error.data.guessesRemaining > 0
      ? { kind: "rejected", guessesRemaining: error.data.guessesRemaining }
      : { kind: "gone" };
  }
  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (status === 410 || status === 404) return { kind: "gone" };
  if (status === 409) return { kind: "notCodeMode" };
  if (status === 429) return { kind: "other", message: VERIFY_LOCKED_OUT_MESSAGE };
  return { kind: "other", message: VERIFY_GENERIC_MESSAGE };
}

function formatCountdown(milliseconds: number): string {
  const remaining = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, "0")}`;
}

function rejectedMessage(guessesRemaining: number): string {
  return `That code is not right. ${guessesRemaining} ${guessesRemaining === 1 ? "try" : "tries"} left.`;
}

export interface ApprovalCodeEntryProps {
  action: BookingApprovalAction;
  /** Epoch milliseconds the approval lapses at, as the page already holds it; null hides the countdown. */
  expiresAt: number | null;
  /** The page's 1-second ticker, so the countdown moves without a timer of its own. */
  nowMs: number;
  onVerify: (code: string) => Promise<void>;
  /** Omitted where the page has no way to ask again without the person retyping something. */
  onResend?: () => void;
  onStartOver: () => void;
  error?: ApprovalCodeError;
  busy: boolean;
  className?: string;
}

/**
 * The one-time code view for BookingApproval:Mode=Code (AB#585), shared by the manage, cancel-link and
 * documents pages in place of their "Answer Your Phone" view. Owns only the typed code; the approval,
 * the countdown's clock and what a verified answer does all stay with the page.
 */
export function ApprovalCodeEntry({
  action,
  expiresAt,
  nowMs,
  onVerify,
  onResend,
  onStartOver,
  error,
  busy,
  className
}: ApprovalCodeEntryProps) {
  const [code, setCode] = useState("");

  const expired = expiresAt !== null && nowMs > expiresAt;
  const complete = /^\d{6}$/.test(code);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!complete || busy || expired) return;
    await onVerify(code);
  };

  return (
    <Card className={className} data-testid="approval-code">
      <CardHeader className="text-center px-4 space-y-4">
        <div className="mx-auto w-16 h-16 bg-sky-100 flex items-center justify-center rounded-full">
          <IconMessage2 className="w-8 h-8 text-sky-600" aria-hidden="true" />
        </div>
        <CardTitle className="text-2xl font-semibold text-slate-900">Enter Your Code</CardTitle>
        <CardDescription className="text-slate-500 text-base">
          {APPROVAL_CODE_SENT_COPY}
          {action === "CancelBooking" && ` ${APPROVAL_CODE_CANCEL_COPY}`}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form className="space-y-4" id="approval-code-form" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="approval-code-input">Six-digit code</Label>
            <Input
              id="approval-code-input"
              name="approvalCode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              aria-invalid={error?.kind === "rejected"}
              aria-describedby={expiresAt !== null ? "approval-code-expiry" : undefined}
              disabled={expired}
            />
            {error && (
              <FieldError data-testid="approval-code-error">
                {error.kind === "rejected" ? rejectedMessage(error.guessesRemaining) : error.message}
              </FieldError>
            )}
          </div>

          {expiresAt !== null && (
            <p id="approval-code-expiry" className="text-sm text-slate-500" aria-live="polite" data-testid="approval-code-expiry">
              {expired
                ? "This code has expired. Send a new code or start over."
                : `Expires in ${formatCountdown(expiresAt - nowMs)}`}
            </p>
          )}
        </form>
      </CardContent>

      <CardFooter className="px-4 pb-6 flex flex-col gap-2">
        <Button
          form="approval-code-form"
          type="submit"
          className="w-full text-base h-12 bg-[#009681] hover:bg-[#007e6c] active:bg-[#006b5b]"
          disabled={!complete || busy || expired}
        >
          {busy ? (
            <span className="flex items-center gap-2">
              <IconLoader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Checking…
            </span>
          ) : (
            "Verify"
          )}
        </Button>
        {onResend && (
          <Button type="button" variant="ghost" className="w-full text-slate-500" disabled={busy} onClick={onResend}>
            Send a new code
          </Button>
        )}
        <Button type="button" variant="ghost" className="w-full text-slate-400" disabled={busy} onClick={onStartOver}>
          Start over
        </Button>
      </CardFooter>
    </Card>
  );
}
